import { MODEL_OPTIONS, ModelOption, DEFAULT_MODEL } from './ModelOptions';
import { MODEL_NAMES, type ModelName } from './modelNames';
import { Industry } from '../types/industry';

// Get all available model names from MODEL_OPTIONS to ensure consistency
const AVAILABLE_MODEL_NAMES = new Set(MODEL_OPTIONS.map((model) => model.modelName));

// Define the preferred model order for each industry
// Models are ordered from best to worst for that industry
// All model names must exist in MODEL_OPTIONS
const INDUSTRY_MODEL_ORDERING_RAW: Partial<Record<Industry, ModelName[]>> = {
  'legal-judiciary': [
    MODEL_NAMES.RECOMMENDED,
    MODEL_NAMES.OPENAI,
    MODEL_NAMES.ANTHROPIC,
    MODEL_NAMES.GEMINI,
    MODEL_NAMES.COHERE,
    MODEL_NAMES.XAI,
  ],
  'banking-financial-services': [
    MODEL_NAMES.RECOMMENDED,
    MODEL_NAMES.OPENAI,
    MODEL_NAMES.ANTHROPIC,
    MODEL_NAMES.GEMINI,
    MODEL_NAMES.COHERE,
    MODEL_NAMES.XAI,
  ],
  'healthcare-life-sciences': [
    MODEL_NAMES.RECOMMENDED,
    MODEL_NAMES.OPENAI,
    MODEL_NAMES.GEMINI,
    MODEL_NAMES.ANTHROPIC,
    MODEL_NAMES.COHERE,
    MODEL_NAMES.XAI,
  ],
  insurance: [
    MODEL_NAMES.RECOMMENDED,
    MODEL_NAMES.OPENAI,
    MODEL_NAMES.ANTHROPIC,
    MODEL_NAMES.GEMINI,
    MODEL_NAMES.COHERE,
    MODEL_NAMES.XAI,
  ],
  'retail-member-services': [
    MODEL_NAMES.RECOMMENDED,
    MODEL_NAMES.OPENAI,
    MODEL_NAMES.GEMINI,
    MODEL_NAMES.ANTHROPIC,
    MODEL_NAMES.COHERE,
    MODEL_NAMES.XAI,
  ],
  'food-beverage': [
    MODEL_NAMES.RECOMMENDED,
    MODEL_NAMES.OPENAI,
    MODEL_NAMES.GEMINI,
    MODEL_NAMES.ANTHROPIC,
    MODEL_NAMES.COHERE,
    MODEL_NAMES.XAI,
  ],
};

// Validate that all model names in ordering exist in MODEL_OPTIONS
const validateModelOrdering = (): void => {
  for (const [industry, modelNames] of Object.entries(INDUSTRY_MODEL_ORDERING_RAW)) {
    for (const modelName of modelNames) {
      if (!AVAILABLE_MODEL_NAMES.has(modelName)) {
        throw new Error(
          `Invalid model name "${modelName}" in industry ordering for "${industry}". ` +
            `Available models: ${Array.from(AVAILABLE_MODEL_NAMES).join(', ')}`,
        );
      }
    }
  }
};

// Validate on module load
validateModelOrdering();

// Export the validated ordering
export const INDUSTRY_MODEL_ORDERING: Partial<Record<Industry, ModelName[]>> =
  INDUSTRY_MODEL_ORDERING_RAW;

/**
 * Get ordered model options based on selected industry
 * @param industry - The selected industry ID (e.g., 'banking-financial-services')
 * @returns Array of ModelOption ordered by preference for that industry
 */
export const getOrderedModelsForIndustry = (
  industry: Industry | string | null | undefined,
): ModelOption[] => {
  // If no industry selected, return default order
  if (!industry) {
    return MODEL_OPTIONS;
  }

  // Get the preferred order for this industry
  const preferredOrder = INDUSTRY_MODEL_ORDERING[industry as Industry];

  // If no specific ordering exists for this industry, return default order
  if (!preferredOrder) {
    return MODEL_OPTIONS;
  }

  // Create a map of modelName -> ModelOption for quick lookup
  const modelMap = new Map(MODEL_OPTIONS.map((model) => [model.modelName, model]));

  // Order models according to preferred order
  const orderedModels: ModelOption[] = [];
  const addedModels = new Set<string>();

  // First, add models in the preferred order
  for (const modelName of preferredOrder) {
    const model = modelMap.get(modelName);
    if (model) {
      orderedModels.push(model);
      addedModels.add(modelName);
    }
  }

  // Then, add any remaining models that weren't in the preferred order
  // (in case new models are added but not yet in the ordering config)
  for (const model of MODEL_OPTIONS) {
    if (!addedModels.has(model.modelName)) {
      orderedModels.push(model);
    }
  }

  return orderedModels;
};

/**
 * Get the top (first/preferred) model for a given industry
 * @param industry - The selected industry ID (e.g., 'banking-financial-services')
 * @returns The model name of the top model for that industry, or DEFAULT_MODEL if no industry
 */
export const getTopModelForIndustry = (
  industry: Industry | string | null | undefined,
): ModelName => {
  // If no industry selected, return default model
  if (!industry) {
    return DEFAULT_MODEL;
  }

  // Get the preferred order for this industry
  const preferredOrder = INDUSTRY_MODEL_ORDERING[industry as Industry];

  // If no specific ordering exists for this industry, return default model
  if (!preferredOrder || preferredOrder.length === 0) {
    return DEFAULT_MODEL;
  }

  // Return the first model in the preferred order
  return preferredOrder[0] as ModelName;
};
